import { type AwardConfirmation } from './AwardConfirmation'
import { type AwardedType } from './AwardedType'
import { type LaneProposalErrors } from './LaneProposalErrors'

export type LaneProposal = {
  id: number
  awardConfirmation?: AwardConfirmation
  awardedType?: AwardedType
  awardedVolume?: number
  capacity?: number
  convertedFlatRate?: string
  convertedPerDistanceRate?: string
  errors?: LaneProposalErrors
  errorStr?: string
  flatRate: string
  laneId: number
  negotiationNote?: string
  perDistanceRate: string
  underNegotiation: boolean
  varianceFromAcceptanceRate?: string
  lanePreferenceId?: string
}

type NewLaneProposalParams = {
  newCapacity: number
  newFlatRate: number
  newPerMileRate: number
}

type LaneProposalErrorData = {
  capacity?: string[]
  per_mile_rate?: string[]
}

export const enum ProposalAction {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}

export function shouldCreateLaneProposal(
  laneProposal: LaneProposal | undefined,
  newLaneProposalParams: NewLaneProposalParams
) {
  if (laneProposal) return false

  const { newFlatRate, newPerMileRate } = newLaneProposalParams

  const hasFlatRate = newFlatRate !== 0
  const hasPerMileRate = newPerMileRate !== 0

  return hasFlatRate || hasPerMileRate
}

export function shouldUpdateLaneProposal(
  laneProposal: LaneProposal | undefined,
  newLaneProposalParams: NewLaneProposalParams
) {
  if (!laneProposal) return false
  const { capacity, flatRate, perDistanceRate } = laneProposal
  const { newCapacity, newFlatRate, newPerMileRate } = newLaneProposalParams

  const hasChangedFlatRate = Number(flatRate) !== newFlatRate
  const hasChangedPerMileRate = Number(perDistanceRate) !== newPerMileRate
  const hasChangedCapacity = Number(capacity) !== newCapacity

  return hasChangedFlatRate || hasChangedPerMileRate || hasChangedCapacity
}

export function shouldDeleteLaneProposal(
  laneProposal: LaneProposal | undefined,
  newLaneProposalParams: NewLaneProposalParams
) {
  if (!laneProposal) return false
  const { flatRate, perDistanceRate } = laneProposal
  const { newFlatRate, newPerMileRate } = newLaneProposalParams

  const hasDeletedFlatRate = Number(flatRate) !== 0 && newFlatRate === 0
  const hasDeletePerMileRate = Number(perDistanceRate) !== 0 && newPerMileRate === 0

  return hasDeletedFlatRate && hasDeletePerMileRate
}

export function getSubmitAction(
  laneProposal: LaneProposal | undefined,
  newLaneProposalParams: NewLaneProposalParams
) {
  if (shouldDeleteLaneProposal(laneProposal, newLaneProposalParams)) {
    return ProposalAction.Delete
  }

  if (shouldUpdateLaneProposal(laneProposal, newLaneProposalParams)) {
    return ProposalAction.Update
  }

  if (shouldCreateLaneProposal(laneProposal, newLaneProposalParams)) {
    return ProposalAction.Create
  }
}

export function hasOutOfSyncError(errorData?: Array<string>) {
  if (!Array.isArray(errorData)) {
    return false
  }

  return errorData[0].toLowerCase().includes('a lane proposal already exists')
}

export function hasErrors(errorData?: LaneProposalErrorData) {
  const { capacity = [], per_mile_rate = [] } = errorData ?? {}

  const hasCapacityError = capacity.length > 0
  const hasRatePerMileError = per_mile_rate.length > 0

  return { hasCapacityError, hasRatePerMileError }
}

export function isFlatRateEmpty(flatRateValue: string) {
  return isNaN(parseInt(flatRateValue))
}

export function isSameFlatRate(flatRateValue: number, savedLaneProposal?: LaneProposal) {
  return savedLaneProposal && flatRateValue === parseInt(savedLaneProposal.flatRate)
}

export function isSameCapacity(capacityValue: number, savedLaneProposal?: LaneProposal) {
  return capacityValue === savedLaneProposal?.capacity
}

export function hasBid(laneProposalIds: Array<number>) {
  return laneProposalIds.length > 0
}

export function shouldSubmitLaneProposal(
  capacityValue: string | number,
  flatRateValue: string | number,
  savedLaneProposal?: LaneProposal
) {
  // It validates when user clicks on input, and clicks outside the input without adding any value
  const flatRateEmpty = isFlatRateEmpty(flatRateValue.toString())
  // It checks if the flat rate was changed (current vs saved)
  const sameFlatRate = isSameFlatRate(parseInt(flatRateValue.toString()), savedLaneProposal)
  // It checks if the capacity was changed (current vs saved)
  const sameCapacity = isSameCapacity(parseInt(capacityValue.toString()), savedLaneProposal)

  const hasValidFlatRate = !flatRateEmpty && !sameFlatRate
  const hasValidCapacity = !flatRateEmpty && !sameCapacity

  return hasValidFlatRate || hasValidCapacity
}

export function isUnderNegotiation(laneProposal?: LaneProposal) {
  if (!laneProposal) return false

  return laneProposal.underNegotiation
}

export function getResultVariance(variance: string) {
  /**
   * as value could be: -0-5% OR 0-5%
   * first one is negative result and second one is positive
   */
  return variance.startsWith('-') ? 'negative' : 'positive'
}

export function hasValidAwardedType(awardedType?: AwardedType) {
  return Boolean(awardedType)
}

export function isRankProposal(laneProposal?: LaneProposal): boolean {
  if (!laneProposal) {
    return false
  }

  return laneProposal.awardConfirmation === 'accepted' && laneProposal.awardedType === 'rank'
}
